import CodeBlock from '../CodeBlock';

function FnSignature({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-left font-mono text-sm bg-gray-900/50 border border-gray-800 rounded-lg px-4 py-2.5 mb-3 text-gray-300 overflow-x-auto">
      {children}
    </div>
  );
}

export default function DocsVizModule() {
  return (
    <section data-docs-section="viz" className="text-left mb-16">
      <h2 className="text-2xl font-bold text-white mb-2 group">
        <a href="#viz" className="hover:text-sky-400 transition-colors">pa.viz</a>
        <span className="ml-2 opacity-0 group-hover:opacity-100 text-gray-600 transition-opacity">#</span>
      </h2>
      <p className="text-gray-400 mb-3">
        Plotly-based charts with dark/light theme support. Requires <code className="text-gray-300">pip install 'propalgos[viz]'</code>.
        All functions return a <code className="text-gray-300">plotly.graph_objects.Figure</code>. In
        Jupyter notebooks, figures auto-render on cell execution.
      </p>

      {/* tearsheet */}
      <div id="viz-tearsheet" data-docs-section="viz-tearsheet" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">tearsheet()</h3>
        <FnSignature>
          pa.viz.tearsheet(result) → Figure
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Multi-panel strategy tearsheet — the signature PropAlgos visualization.
          Renders equity curve (strategy vs benchmark), drawdown chart, monthly
          returns heatmap, and a key-metrics annotation block. Auto-calls <code className="text-gray-300">fig.show()</code> in
          Jupyter notebooks.
        </p>
        <div className="space-y-0 mb-3 text-left">
          <div className="flex gap-2 text-sm py-1.5 border-b border-gray-800/50 last:border-0">
            <code className="text-sky-400 shrink-0">result</code>
            <span className="text-gray-600 shrink-0">BacktestResult</span>
            <span className="text-gray-400">Any object with a <code className="text-gray-300">.returns</code> attribute. Optionally <code className="text-gray-300">.benchmark_returns</code>, <code className="text-gray-300">.name</code>, <code className="text-gray-300">.trades</code>.</span>
          </div>
        </div>
        <CodeBlock
          code={`result = pa.backtest.run(prices, signal, benchmark="SPY")
pa.viz.tearsheet(result)  # auto-renders in notebooks`}
        />
      </div>

      {/* candles */}
      <div id="viz-candles" data-docs-section="viz-candles" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">candles()</h3>
        <FnSignature>
          pa.viz.candles(prices, indicators=None, signals=None, title=None) → Figure
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Interactive candlestick chart with optional indicator overlays and trade signal markers.
        </p>
        <div className="space-y-0 mb-3 text-left">
          <div className="flex gap-2 text-sm py-1.5 border-b border-gray-800/50">
            <code className="text-sky-400 shrink-0">prices</code>
            <span className="text-gray-600 shrink-0">DataFrame</span>
            <span className="text-gray-400">Must contain open, high, low, close columns.</span>
          </div>
          <div className="flex gap-2 text-sm py-1.5 border-b border-gray-800/50">
            <code className="text-sky-400 shrink-0">indicators</code>
            <span className="text-gray-600 shrink-0">list[str] | None</span>
            <span className="text-gray-400">Indicator specs: <code className="text-gray-300">"sma20"</code>, <code className="text-gray-300">"ema50"</code>, <code className="text-gray-300">"bb20"</code> (Bollinger Bands).</span>
          </div>
          <div className="flex gap-2 text-sm py-1.5 border-b border-gray-800/50">
            <code className="text-sky-400 shrink-0">signals</code>
            <span className="text-gray-600 shrink-0">Series | None</span>
            <span className="text-gray-400">+1 = green buy triangle, -1 = red sell triangle.</span>
          </div>
          <div className="flex gap-2 text-sm py-1.5 border-b border-gray-800/50 last:border-0">
            <code className="text-sky-400 shrink-0">title</code>
            <span className="text-gray-600 shrink-0">str | None</span>
            <span className="text-gray-400">Chart title. Defaults to "Price".</span>
          </div>
        </div>
        <CodeBlock
          code={`fig = pa.viz.candles(
    prices,
    indicators=["sma20", "sma100", "bb20"],
    signals=signal,
    title="AAPL with SMA Crossover",
)
fig.show()`}
        />
      </div>

      {/* heatmaps */}
      <div id="viz-heatmaps" data-docs-section="viz-heatmaps" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">Heatmaps</h3>
        <FnSignature>
          pa.viz.monthly_returns(result_or_returns) → Figure
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Monthly returns heatmap. Accepts a BacktestResult or a raw return Series.
          Rows = years, columns = months (Jan-Dec).
        </p>
        <CodeBlock
          code={`fig = pa.viz.monthly_returns(result)
fig.show()`}
        />
        <FnSignature>
          pa.viz.heatmap(z, colorscale="diverging", fmt=".1%", title="", zrange=None) → Figure
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          General-purpose heatmap for any DataFrame matrix. Colorscale presets:
          <code className="text-gray-300">"diverging"</code> (red → green),
          <code className="text-gray-300">"sequential"</code> (green → red),
          <code className="text-gray-300">"rsi"</code> (green → neutral → red).
        </p>
        <CodeBlock
          code={`# RSI heatmap across symbols and timeframes
fig = pa.viz.heatmap(rsi_matrix, colorscale="rsi", fmt=".0f", zrange=(0, 100))
fig.show()`}
        />
      </div>

      {/* strip */}
      <div id="viz-strip" data-docs-section="viz-strip" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">strip()</h3>
        <FnSignature>
          pa.viz.strip(data, x, y, color, thresholds=None, title="", y_range=None) → Figure
        </FnSignature>
        <p className="text-gray-400 text-sm mb-3">
          Strip (jitter) plot for signal distribution analysis. Each point is a data
          observation, grouped by categorical x-axis, colored by a grouping column.
          Optional threshold bands highlight zones of interest.
        </p>
        <CodeBlock
          code={`fig = pa.viz.strip(
    data,
    x="timeframe",
    y="rsi",
    color="symbol",
    thresholds={"Oversold": (0, 30), "Overbought": (70, 100)},
    y_range=(0, 100),
)
fig.show()`}
        />
      </div>

      {/* Theming */}
      <div id="viz-theming" data-docs-section="viz-theming" className="mb-10">
        <h3 className="text-lg font-semibold text-white mb-2">Theming</h3>
        <p className="text-gray-400 text-sm mb-3">
          All charts use a global dark theme by default. Switch to light mode for
          presentations or export.
        </p>
        <div className="overflow-x-auto mb-3">
          <table className="text-sm text-left w-full">
            <thead>
              <tr className="border-b border-gray-800">
                <th className="py-2 pr-4 text-gray-400 font-medium">Function</th>
                <th className="py-2 text-gray-400 font-medium">Description</th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">set_mode("dark" | "light")</td>
                <td className="py-2 text-gray-400">Switch global theme. Affects all subsequent charts.</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">apply_theme(fig) → Figure</td>
                <td className="py-2 text-gray-400">Stamp PropAlgos theme onto any Plotly figure.</td>
              </tr>
              <tr className="border-b border-gray-800/50">
                <td className="py-2 pr-4 font-mono text-sky-400">colors() → dict</td>
                <td className="py-2 text-gray-400">Active color palette (bg, surface, text, accent, positive, negative, ...).</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-mono text-sky-400">colorscales() → dict</td>
                <td className="py-2 text-gray-400">Active colorscale presets (diverging, sequential, rsi).</td>
              </tr>
            </tbody>
          </table>
        </div>
        <CodeBlock
          code={`# Switch to light mode for export
pa.viz.set_mode("light")

# Apply theme to a custom figure
import plotly.graph_objects as go
fig = go.Figure(go.Scatter(x=[1,2,3], y=[1,3,2]))
fig = pa.viz.apply_theme(fig)

# Access palette colors
palette = pa.viz.colors()
print(palette["accent"])  # "#6366f1"`}
        />
      </div>
    </section>
  );
}
